import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useRefreshSession, useSession } from '../../lib/session';
import { errorText } from '../../i18n';
import { Logo } from '@vatman/ui';
import { LegalChecks } from './Legal';
import './auth.css';

type Mode = 'login' | 'register' | 'forgot';

export function AuthHero() {
  const { t } = useTranslation();
  return (
    <aside className="auth-hero">
      <div className="brand">
        <Logo size={30} /> {t('app.name')}
      </div>
      <div className="hero-board" aria-hidden="true">
        <div className="hero-note" style={{ left: 20, top: 30, background: '#FFE066' }}>
          {t('templates.content.idea')} 1
        </div>
        <div className="hero-note" style={{ left: 160, top: 70, background: '#B8D4FF', animationDelay: '-2s' }}>
          {t('templates.content.idea')} 2
        </div>
        <div className="hero-note" style={{ left: 300, top: 20, background: '#C3EFA6', animationDelay: '-4s' }}>
          {t('templates.content.task')}
        </div>
        <div className="hero-note" style={{ left: 90, top: 180, background: '#FFB3BA', animationDelay: '-1s' }}>
          {t('templates.content.retroGood')}
        </div>
        <div className="hero-cursor" style={{ left: 250, top: 160 }}>
          <svg width="16" height="22" viewBox="0 0 16 22">
            <path d="M0 0v17l4.5-4 3 7 3-1.2-2.9-6.6H13z" fill="#F76B15" stroke="#fff" strokeWidth="1.5" />
          </svg>
          <span style={{ background: '#F76B15' }}>Аня</span>
        </div>
        <div className="hero-cursor" style={{ left: 40, top: 120, animationDelay: '-4s' }}>
          <svg width="16" height="22" viewBox="0 0 16 22">
            <path d="M0 0v17l4.5-4 3 7 3-1.2-2.9-6.6H13z" fill="#12A594" stroke="#fff" strokeWidth="1.5" />
          </svg>
          <span style={{ background: '#12A594' }}>Олег</span>
        </div>
      </div>
      <div>
        <h1>{t('auth.heroTitle')}</h1>
        <p>{t('auth.heroText')}</p>
      </div>
    </aside>
  );
}

export function AuthPage({ mode }: { mode: Mode }) {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const refresh = useRefreshSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState({ terms: false, pd: false });
  const legal = session.data?.legal;

  if (session.data?.user) return <Navigate to={next} replace />;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorText(e instanceof ApiError ? e.code : 'internal'));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (mode === 'login') {
        await api.post('/auth/login', { email, password });
      } else if (mode === 'register') {
        await api.post('/auth/register', { email, password, name, locale: i18n.language, consent: !!legal && agreed.terms && agreed.pd });
      } else {
        await api.post('/auth/forgot', { email, locale: i18n.language });
        setNotice(t('auth.forgotSent'));
        return;
      }
      await refresh();
      navigate(next, { replace: true });
    });
  };

  const magic = () => {
    if (!email) {
      setError(errorText('invalid_email'));
      return;
    }
    void run(async () => {
      await api.post('/auth/magic', { email, locale: i18n.language });
      setNotice(t('auth.magicSent', { email }));
    });
  };

  const oauth = session.data?.oauth;
  const nextQuery = next !== '/' ? `?next=${encodeURIComponent(next)}` : '';

  return (
    <div className="auth">
      <AuthHero />
      <main className="auth-main">
        <form className="auth-card" onSubmit={submit}>
          <h2>{mode === 'login' ? t('auth.loginTitle') : mode === 'register' ? t('auth.registerTitle') : t('auth.forgotTitle')}</h2>
          {mode === 'forgot' && <p className="muted" style={{ margin: 0 }}>{t('auth.forgotHint')}</p>}
          {notice && <div className="auth-note">{notice}</div>}

          {mode !== 'forgot' && (oauth?.yandex || oauth?.vk) && (
            <>
              <div className="oauth-row">
                {oauth.yandex && (
                  <a className="btn lg block" href={`/api/auth/oauth/yandex/start?next=${encodeURIComponent(next)}`}>
                    <span style={{ color: '#FC3F1D', fontWeight: 800 }}>Я</span> {t('auth.withYandex')}
                  </a>
                )}
                {oauth.vk && (
                  <a className="btn lg block" href={`/api/auth/oauth/vk/start?next=${encodeURIComponent(next)}`}>
                    <span style={{ color: '#0077FF', fontWeight: 800 }}>VK</span> {t('auth.withVk')}
                  </a>
                )}
              </div>
              <div className="auth-divider">{t('common.or')}</div>
            </>
          )}

          {mode === 'register' && (
            <label className="field">
              <span>{t('auth.name')}</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('auth.namePlaceholder')} autoComplete="name" required autoFocus />
            </label>
          )}
          <label className="field">
            <span>{t('auth.email')}</span>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.ru"
              autoComplete="email"
              required
              autoFocus={mode !== 'register'}
            />
          </label>
          {mode !== 'forgot' && (
            <label className="field">
              <span style={{ display: 'flex', justifyContent: 'space-between' }}>
                {t('auth.password')}
                {mode === 'login' && (
                  <Link to="/forgot" style={{ fontWeight: 500 }}>
                    {t('auth.forgot')}
                  </Link>
                )}
              </span>
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                minLength={8}
                required
              />
            </label>
          )}
          {mode === 'register' && legal && <LegalChecks legal={legal} terms={agreed.terms} pd={agreed.pd} onChange={setAgreed} />}
          {error && <div className="error-text">{error}</div>}
          <button className="btn primary lg block" disabled={busy || (mode === 'register' && !!legal && !(agreed.terms && agreed.pd))}>
            {mode === 'login' ? t('auth.login') : mode === 'register' ? t('auth.register') : t('auth.send')}
          </button>
          {mode === 'login' && (
            <button type="button" className="btn lg block" onClick={magic} disabled={busy}>
              <Mail size={17} /> {t('auth.magic')}
            </button>
          )}
          {mode === 'register' && !legal && <p className="subtle" style={{ fontSize: 'var(--fs-2)', margin: 0 }}>{t('auth.terms')}</p>}
          <div className="auth-switch">
            {mode === 'login' ? (
              <>
                {t('auth.noAccount')} <Link to={`/register${nextQuery}`}>{t('auth.register')}</Link>
              </>
            ) : (
              <>
                {t('auth.haveAccount')} <Link to={`/login${nextQuery}`}>{t('auth.login')}</Link>
              </>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}
